/*
 * Copyright (c) 2019 by Enrico Ansaloni <enrico.ansaloni@gmail.com>
 */

import { EventEmitter } from 'events';
import AppController from "./AppController";

/*
 EVENT LIST:
 authNeededChanged
 loggedInChanged
 authChanged
 loginSuccessful
 loginFailed
 logoutSuccessful
 */

export class AuthController extends EventEmitter {

    app: any = null;
    loggedIn: boolean = false;
    authNeeded: boolean = false;
    status: string = "ok";
    errors: any = null;
    message: string = "";
    auth: any = {
        userId: null,
        isAdmin: false,
        isManager: false,
        isSuperuser: false,
        accessToken: null,
        refreshToken: null
    };

    constructor(app: AppController) {
        super();
        // sync app controller reference
        this.app = app;
        // load state
        let auth = AppController.getValueFromStorage("auth");
        if (auth) {
            this.setAuth(auth);
        }
        let loggedIn = AppController.getValueFromStorage("loggedIn");
        if (loggedIn)
            this.setLoggedIn(loggedIn);
        let authNeeded = AppController.getValueFromStorage("authNeeded");
        if (authNeeded)
            this.setAuthNeeded(authNeeded);
    }

    // getters and setters with events
    isAuthNeeded = () => {
        return this.authNeeded;
    };

    setAuthNeeded = (value: boolean) => {
        if (this.authNeeded === value)
            return;
        this.authNeeded = value;
        this.emit("authNeededChanged");
    };

    isLoggedIn = () => {
        return this.loggedIn;
    };

    setLoggedIn = (value: boolean) => {
        if (this.loggedIn === value) {
            return;
        }
        this.loggedIn = value;
        this.emit("loggedInChanged");
    };

    getAuth = () => {
        return this.auth;
    };

    setAuth = (value: any) => {
        let keys = ["userId", "isAdmin", "isManager", "isSuperuser", "accessToken", "refreshToken"];
        let modified: boolean = false;
        if (value) {
            for (const i in keys) {
                let key: string = keys[i];
                let oldValue = this.auth[key];
                let newValue = value[key];
                if (oldValue !== newValue) {
                    modified = true;
                    break;
                }
            }
        }
        if (modified) {
            this.auth = value;
            this.emit("authChanged");
        }
    };

    login = async (user: any) => {
        let passed: boolean = false;

        try {
            let result = await this.app.rest.postData("auth/login", user);
            // process result
            if (result.status === "ok") {
                // store accesstoken and set flags
                console.log(`AuthController.tsx: login: logged in successfully`);
                this.status = result.status;
                this.message = result.message;
                this.setAuth({
                    userId: result.userId,
                    isAdmin: result.isAdmin,
                    isManager: result.isManager,
                    isSuperuser: result.isSuperuser,
                    accessToken: result.accessToken,
                    refreshToken: result.refreshToken
                });
                this.setAuthNeeded(false);
                this.setLoggedIn(true);
                await AppController.setValueToStorage("auth", this.auth);
                await AppController.setValueToStorage("loggedIn", true);
                await AppController.setValueToStorage("authNeeded", false);
                passed = true;

            } else {
                console.log(`AuthController.tsx: login: failed to log in`);
                // manage error
                this.status = result.status;
                this.errors = result.errors;
                this.message = result.message;
                this.setAuth({
                    userId: null,
                    isAdmin: false,
                    isManager: false,
                    accessToken: null,
                    refreshToken: null
                });
                this.setLoggedIn(false);
                await AppController.setValueToStorage("loggedIn", false);
                AppController.clearValueFromStorage("auth");
            }

        } catch (error) {
            console.log(`AuthController.tsx: login: HTTP error: ${error}`);
            // manage error
            this.status = "error";
            this.errors = error;
            this.message = `HTTP ERROR: ${error}`;
            this.setAuth({
                userId: null,
                isAdmin: false,
                isManager: false,
                accessToken: null,
                refreshToken: null
            });
            this.setLoggedIn(false);
            await AppController.setValueToStorage("loggedIn", false);
            AppController.clearValueFromStorage("auth");
        }

        if (passed) {
            this.emit("loginSuccessful");
        } else {
            this.emit("loginFailed");
        }

        return passed;
    }

    logout = async () => {
        this.status = "ok";
        this.message = "Logout successful";
        this.setAuth({
            userId: null,
            isAdmin: false,
            isManager: false,
            accessToken: null,
            refreshToken: null
        });
        this.setLoggedIn(false);
        await AppController.setValueToStorage("loggedIn", false);
        AppController.clearValueFromStorage("auth");
        this.emit("logoutSuccessful");
    };

}

export default AuthController;
